// frontend/src/components/CaseWorkstation.jsx
// Version: 2.0 (quick-260915-did — выдуманная фактура дела убрана)
// Description: Интеллектуальное рабочее место по делу в дизайне Fitenko Law
//              (Quiet Luxury Editorial): стратегия, чеклист обязательных улик USCIS,
//              статусы готовности пакета, таймлайн и интеграция с Katifi.
//
// 🔴 ПОЧЕМУ ЭТО БЫЛО ОПАСНЕЕ СПИСКА (15.09.2026, quick-260915-did). Здесь
//    выдумка стояла УМОЛЧАНИЯМИ и подставлялась ПОВЕРХ НАСТОЯЩЕГО ДЕЛА, когда
//    поле пусто: чужое имя клиента, чужой номер дела, чужой срок. Экран
//    показывается на боевом маршруте /cases/:id арендатора fitenko
//    (pages/CaseDetailPage.js), то есть подпись «Клиент: …» под настоящим делом
//    могла назвать человека, которого не существует.
//
// 🔴 УБРАНО ЦЕЛИКОМ, А НЕ ПОДМЕНЕНО ДРУГИМ ОБРАЗЦОМ: стратегия, «следующий
//    лучший шаг», стенограмма «последнего звонка», четыре майлстоуна с датами,
//    состав команды, оценка рисков и имена файлов в чеклисте были написаны
//    заранее и НЕ ИЗМЕРЯЛИСЬ ничем. Живого источника у них нет, изобретать
//    ручку бэкенда эта задача не вправе — значит на их месте стоит ЧЕСТНОЕ
//    «источника нет», а не правдоподобный текст.
//
// 🔴 ПУСТОЕ ПОЛЕ ПОКАЗЫВАЕТСЯ ПУСТЫМ. `—` и «не указано» — это ответ «в деле
//    этого нет»; подстановка чужого значения — ответ «вот оно», и он ложный.

import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  Briefcase,
  Calendar,
  CheckCircle2,
  AlertTriangle,
  FileText,
  Clock,
  Sparkles,
  Upload,
  User,
  ShieldAlert,
  ChevronRight,
  ExternalLink,
  Plus,
  Building,
  Scale
} from 'lucide-react';

// ТРЕБОВАНИЯ USCIS ПО КАТЕГОРИЯМ ДЕЛ — это перечень того, ЧТО НУЖНО, а не
// состояние дела. Здесь НЕТ имён файлов, источников и статусов: прежние
// («Passport_Sofia_Martinez.pdf • проверено AI Paralegal», «срок истекает через
// 18 дней», «48 страниц») были ВЫДУМАНЫ и читались как измеренные.
// Ручки, которая сказала бы, что из этого в деле СОБРАНО, не существует —
// поэтому статус каждого пункта начинается с «не отмечен».
const TREBOVANIYA_USCIS = {
  'e-2': [
    { id: 'doc-1', title: 'Паспорт и подтверждение гражданства договорной страны', required: true },
    { id: 'doc-2', title: 'Форма I-94 и актуальный статус пребывания', required: true },
    { id: 'doc-3', title: 'Договор аренды коммерческого помещения', required: true },
    { id: 'doc-4', title: 'Подтверждение инвестиций и происхождение средств', required: true },
    { id: 'doc-5', title: 'Бизнес-план и прогноз создания рабочих мест', required: true }
  ],
  'i-130': [
    { id: 'doc-1', title: 'Свидетельство о заключении брака', required: true },
    { id: 'doc-2', title: 'Доказательства гражданства США петиционера (паспорт/сертификат)', required: true },
    { id: 'doc-3', title: 'Совместные финансовые обязательства (joint bank accounts, lease)', required: true },
    { id: 'doc-4', title: 'Свидетельства bona fide брака (фотографии, переписка, билеты)', required: true },
    { id: 'doc-5', title: 'Форма I-864 (Affidavit of Support) и налоговые декларации', required: true }
  ]
};

// Единая надпись там, где живого источника НЕТ. Она обязана быть ОДНА на весь
// экран: два разных текста о одном и том же читаются как два разных положения
// дел.
const ISTOCHNIKA_NET = 'Источника нет: в деле это не заведено, ручки бэкенда не существует.';

export const CaseWorkstation = ({
  caseData,
  onBack,
  onAskKatifi,
  onUpdateDeadline,
  onAssignTeam
}) => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'documents' | 'plan'
  
  // Определение типа чеклиста по названию или категории дела
  const caseTypeKey = useMemo(() => {
    const title = (caseData?.title || '').toLowerCase();
    const cat = (caseData?.case_type || '').toLowerCase();
    if (title.includes('130') || cat.includes('marriage') || cat.includes('семейн')) return 'i-130';
    return 'e-2'; // default: E-2 investor
  }, [caseData]);

  // Интерактивный список требований. 🔴 Статус начинается с 'unknown' —
  // «не отмечен», а не 'ready': прежнее значение утверждало, что документ
  // проверен, не спросив об этом никого.
  const [documents, setDocuments] = useState(
    (TREBOVANIYA_USCIS[caseTypeKey] || TREBOVANIYA_USCIS['e-2']).map((d) => ({ ...d, status: 'unknown' }))
  );

  // Переключение статуса документа
  const cycleDocumentStatus = (docId) => {
    setDocuments((prev) =>
      prev.map((doc) => {
        if (doc.id === docId) {
          const nextStatus =
            doc.status === 'unknown' ? 'ready'
              : doc.status === 'ready' ? 'review'
              : doc.status === 'review' ? 'missing'
              : 'unknown';
          return { ...doc, status: nextStatus };
        }
        return doc;
      })
    );
    // 🔴 Отметка живёт ТОЛЬКО в этом окне и исчезнет при обновлении страницы.
    //    Прежнее «Статус документа обновлен» обещало сохранение, которого никто
    //    не исполняет — обещание без исполнителя хуже молчания.
    toast('Отмечено только в этом окне: ручки сохранения статуса в деле нет.', { icon: '📄' });
  };

  // Метрики готовности. 🔴 Пока ни один пункт не отмечен рукой, «готово 0 из 5»
  //    — это ИЗМЕРЕНИЕ, которого не было; в этом случае печатается отсутствие
  //    источника, а не ноль.
  const readyCount = documents.filter((d) => d.status === 'ready').length;
  const reviewCount = documents.filter((d) => d.status === 'review').length;
  const missingCount = documents.filter((d) => d.status === 'missing').length;
  const totalCount = documents.length;
  const otmechenoRukoi = documents.some((d) => d.status !== 'unknown');

  // 🔴 Ни одного умолчания с чужими данными. Пустое поле показывается пустым.
  const caseTitle = caseData?.title || 'Название дела не указано';
  const caseNumber = caseData?.case_number || '—';
  const clientName = caseData?.client_name || caseData?.client?.name || 'не указан';
  const attorneyName = caseData?.assigned_attorney?.name || 'не назначен';
  const deadlineDate = caseData?.deadline || null;

  return (
    <div className="max-w-7xl mx-auto px-4 py-6 space-y-6 text-[#20231F]">
      {/* 1. Навигация "Назад" */}
      <div>
        <button
          onClick={() => (onBack ? onBack() : navigate('/cases'))}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#687077] hover:text-[#20231F] transition-colors cursor-pointer"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Все дела
        </button>
      </div>

      {/* 2. Главная карточка дела: Шапка и ключевые метаданные */}
      <div className="bg-white border border-[#E5E1D6] rounded-2xl p-6 shadow-xs space-y-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="space-y-1">
            <div className="flex flex-wrap items-center gap-2 text-xs">
              <span className="font-bold text-[#83692A] bg-[#FAF4E5] px-2.5 py-0.5 rounded border border-[#C8A653]/30">
                Дело {caseNumber}
              </span>
              <span className="px-2 py-0.5 rounded bg-[#EBF2FD] text-[#3674D8] font-bold text-[11px]">
                {caseTypeKey === 'e-2' ? 'E-2 Treaty Investor' : 'I-130 Marriage Green Card'}
              </span>
              <span className="inline-flex items-center gap-1 text-[11px] text-[#0B8B5E] font-bold">
                <span className="w-2 h-2 rounded-full bg-[#0B8B5E]" />
                Открыто
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-serif font-normal text-[#20231F] pt-1">
              {caseTitle}
            </h1>

            <div className="flex flex-wrap items-center gap-4 text-xs text-[#687077] pt-1">
              <span className="flex items-center gap-1.5">
                <User className="w-3.5 h-3.5 text-[#20231F]" />
                <strong className="text-[#20231F]">Клиент:</strong> {clientName}
              </span>
              <span>•</span>
              <span className="flex items-center gap-1.5">
                <Briefcase className="w-3.5 h-3.5 text-[#20231F]" />
                <strong className="text-[#20231F]">Ведущий адвокат:</strong> {attorneyName}
              </span>
              <span>•</span>
              <span className="flex items-center gap-1.5">
                <Building className="w-3.5 h-3.5 text-[#20231F]" />
                {caseData?.office_name || 'офис не указан'}
              </span>
            </div>
          </div>

          {/* Кнопки быстрых действий по делу */}
          <div className="flex flex-wrap items-center gap-2.5 shrink-0">
            <button
              onClick={() => {
                const targetCaseId = caseData?.id || '';
                navigate(`/ai-ops?tab=council&case=${targetCaseId}&title=${encodeURIComponent(caseTitle)}&client=${encodeURIComponent(clientName)}`);
              }}
              className="px-3.5 py-2 bg-[#FAF4E5] hover:bg-[#F4E9CD] text-[#20231F] border border-[#C8A653]/60 font-bold text-xs rounded-lg shadow-2xs inline-flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <Scale className="w-4 h-4 text-[#83692A]" />
              AI-Консилиум по делу
            </button>
            <button
              onClick={() => {
                if (onAskKatifi) {
                  onAskKatifi(`Запусти AI-консилиум по делу ${caseNumber} (${clientName}) и проверь риски отказа.`);
                } else {
                  toast('Katifi готовит аналитическую сводку по делу...', { icon: '✨' });
                }
              }}
              className="px-3.5 py-2 bg-[#C8A653] hover:bg-[#B8933E] text-[#20231F] font-bold text-xs rounded-lg shadow-sm inline-flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <Sparkles className="w-4 h-4" />
              Запустить AI-разбор
            </button>
          </div>
        </div>

        {/* 3. Предупреждение о критическом дедлайне (I-94 / RFE / Court date) */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-4 pt-2">
          {/* 🔴 Срок печатается ТОЛЬКО когда он есть в деле. Прежде здесь стояли
              выдуманная дата и бейдж «18 дней», НЕ ВЫЧИСЛЕННЫЙ ни из чего:
              относительный срок лжёт уже в момент чтения
              ([[knowledge_relative_deadline_outlives_itself]]). */}
          <div className="md:col-span-8 bg-[#FAF4E5] border border-[#C8A653]/40 rounded-xl p-4 flex items-start gap-3.5">
            <AlertTriangle className="w-5 h-5 text-[#D78416] shrink-0 mt-0.5" />
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <h3 className="text-xs font-bold text-[#83692A] uppercase tracking-wider">
                  Срок по делу
                </h3>
              </div>
              <p className="text-xs text-[#20231F] leading-relaxed">
                {deadlineDate
                  ? <>Срок, записанный в деле: <strong>{deadlineDate}</strong>.</>
                  : 'Срок в деле не указан. Это НЕ значит «срока нет» — значит, его сюда никто не записал.'}
              </p>
            </div>
          </div>

          <div className="md:col-span-4 bg-[#F8F7F2] border border-[#E5E1D6] rounded-xl p-4 flex flex-col justify-between">
            <div>
              <span className="text-[10px] uppercase font-bold text-[#687077] tracking-wider block">
                Готовность пакета USCIS
              </span>
              <div className="text-lg font-bold text-[#20231F] mt-0.5">
                {otmechenoRukoi ? `${readyCount} из ${totalCount} готовы` : '—'}
              </div>
            </div>
            <div className="w-full bg-[#E5E1D6] h-2 rounded-full overflow-hidden mt-2">
              <div
                className="bg-[#0B8B5E] h-full transition-all duration-500"
                style={{ width: `${otmechenoRukoi ? (readyCount / totalCount) * 100 : 0}%` }}
              />
            </div>
            <span className="text-[10px] text-[#687077] mt-2 block leading-relaxed">
              {otmechenoRukoi
                ? `Ещё ${reviewCount} на проверке и ${missingCount} ожидаются от клиента. Отметки сделаны вручную в этом окне и нигде не сохранены.`
                : ISTOCHNIKA_NET}
            </span>
          </div>
        </div>
      </div>

      {/* 4. Табы разделов Workstation */}
      <div className="flex items-center gap-2 border-b border-[#E5E1D6] pb-1 text-xs font-bold">
        {[
          { id: 'overview', label: 'Обзор дела', icon: Briefcase },
          { id: 'documents', label: `Документы и улики (${readyCount}/${totalCount})`, icon: FileText },
          { id: 'plan', label: 'План дела и майлстоуны', icon: Calendar }
        ].map((tab) => {
          const IconC = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2.5 rounded-lg flex items-center gap-2 transition-all cursor-pointer ${
                isActive
                  ? 'bg-[#20231F] text-white shadow-xs'
                  : 'bg-white border border-[#E5E1D6] text-[#687077] hover:text-[#20231F] hover:bg-[#F8F7F2]'
              }`}
            >
              <IconC className="w-3.5 h-3.5" />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* 5. Контент вкладок */}

      {/* ВКЛАДКА 1: ОБЗОР */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
          {/* Левая часть: Стратегия и следующий шаг */}
          <div className="lg:col-span-8 space-y-5">
            {/* Стратегическая цель */}
            <div className="bg-white border border-[#E5E1D6] rounded-xl p-5 space-y-3 shadow-xs">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#687077]">
                Стратегическая цель дела
              </h3>
              {/* 🔴 Прежде здесь стояла стратегия ОДНОГО выдуманного дела — с
                  суммой инвестиции и числом рабочих мест. Под настоящим делом
                  она читалась как стратегия ЭТОГО дела. */}
              <p className="text-sm text-[#687077] leading-relaxed">
                {caseData?.strategy || caseData?.description || ISTOCHNIKA_NET}
              </p>
            </div>

            {/* Следующий лучший шаг от Katifi */}
            <div className="bg-[#FAF4E5] border border-[#C8A653]/30 rounded-xl p-5 space-y-3 shadow-xs">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-bold text-[#83692A]">
                  <Sparkles className="w-4 h-4 text-[#C8A653]" />
                  Следующий лучший шаг (Next Best Action)
                </div>
                <span className="text-[10px] font-extrabold px-2 py-0.5 rounded bg-white text-[#83692A] border border-[#C8A653]/20">
                  AI Recommendation
                </span>
              </div>
              {/* 🔴 «Рекомендация AI», написанная заранее, — не рекомендация:
                  её никто не считал. Ручки, выдающей next best action по делу,
                  не существует. */}
              <p className="text-xs text-[#687077] leading-relaxed">
                {ISTOCHNIKA_NET} Разбор по делу можно запросить кнопкой
                «Запустить AI-разбор» — тогда ответ будет получен, а не написан
                заранее.
              </p>
            </div>

            {/* Последнее обращение клиента */}
            <div className="bg-white border border-[#E5E1D6] rounded-xl p-5 space-y-3 shadow-xs">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#687077]">
                Последний контекст от клиента
              </h3>
              {/* 🔴 Здесь стояла ВЫДУМАННАЯ стенограмма звонка с датой и
                  длительностью — та же, что была в ленте /inbox. Под настоящим
                  делом она читалась как последний разговор С ЭТИМ клиентом. */}
              <div className="bg-[#F8F7F2] p-4 rounded-xl border border-[#E5E1D6]/60 text-xs text-[#687077] space-y-1.5">
                <p className="leading-relaxed">
                  {ISTOCHNIKA_NET} Журнал звонков и писем этого экрана не
                  привязан к делу — смотреть его нужно там, где он живой.
                </p>
                <div className="pt-2 flex items-center justify-end">
                  <button
                    onClick={() => navigate('/inbox')}
                    className="text-xs font-bold text-[#3674D8] hover:underline inline-flex items-center gap-1"
                  >
                    Перейти к ответу в Inbox →
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Правая часть: Команда и аудит рисков */}
          <div className="lg:col-span-4 space-y-5">
            {/* Карточка команды */}
            <div className="bg-white border border-[#E5E1D6] rounded-xl p-5 space-y-4 shadow-xs">
              <h3 className="text-xs font-bold uppercase tracking-wider text-[#687077]">
                Команда дела
              </h3>

              {/* 🔴 Состав команды берётся ИЗ ДЕЛА. Прежде здесь были зашиты
                  три карточки с именами — они стояли под ЛЮБЫМ делом, включая
                  те, где назначен другой адвокат или не назначен никто. */}
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F1EEE4] text-[#687077] font-bold text-xs flex items-center justify-center">
                    <User className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-[#20231F]">{attorneyName}</div>
                    <div className="text-[11px] text-[#687077]">Ведущий адвокат</div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#F1EEE4] text-[#687077] font-bold text-xs flex items-center justify-center">
                    <User className="w-4 h-4" />
                  </div>
                  <div>
                    <div className="text-xs font-bold text-[#20231F]">
                      {caseData?.assigned_paralegal
                        ? `${caseData.assigned_paralegal.first_name || ''} ${caseData.assigned_paralegal.last_name || ''}`.trim() || 'не назначен'
                        : 'не назначен'}
                    </div>
                    <div className="text-[11px] text-[#687077]">Paralegal</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Панель рисков отказа (USCIS Denial Risk Signals) */}
            <div className="bg-white border border-[#E5E1D6] rounded-xl p-5 space-y-3 shadow-xs">
              <div className="flex items-center gap-2 text-xs font-bold text-[#C84842]">
                <ShieldAlert className="w-4 h-4" />
                Оценка рисков отказа USCIS
              </div>
              {/* 🔴 Прежде здесь стояли два «сигнала риска» с пометкой
                  «Оценка рисков отказа USCIS» — их никто не считал по этому
                  делу. Оценка, которой не было, печатается как отсутствие. */}
              <p className="text-xs text-[#687077] leading-relaxed">
                {ISTOCHNIKA_NET} Риски по делу здесь не измеряются; вывод даёт
                AI-консилиум, и его надо запросить.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* ВКЛАДКА 2: ДОКУМЕНТЫ И ЧЕКЛИСТ УЛИК */}
      {activeTab === 'documents' && (
        <div className="bg-white border border-[#E5E1D6] rounded-2xl p-6 shadow-xs space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-[#E5E1D6]">
            <div>
              <h2 className="text-lg font-bold text-[#20231F]">
                Чеклист обязательных доказательств (Evidence Vault)
              </h2>
              <p className="text-xs text-[#687077] mt-0.5 leading-relaxed">
                Это перечень ТРЕБОВАНИЙ USCIS по категории дела, а не состояние
                этого дела. {ISTOCHNIKA_NET} Бейдж статуса переключается вручную
                и живёт только в этом окне.
              </p>
            </div>

            <button
              onClick={() => toast('Открыт проводник безопасной загрузки документов...', { icon: '📄' })}
              className="px-3.5 py-2 bg-[#20231F] hover:bg-black text-white font-bold text-xs rounded-lg shadow-sm inline-flex items-center gap-1.5 transition-all cursor-pointer"
            >
              <Upload className="w-3.5 h-3.5" />
              Загрузить документ в дело
            </button>
          </div>

          <div className="space-y-3">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="p-4 rounded-xl border border-[#E5E1D6] bg-white hover:border-[#C8A653]/60 transition-all flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
              >
                <div className="flex items-start gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-lg bg-[#F8F7F2] border border-[#E5E1D6] flex items-center justify-center shrink-0 mt-0.5">
                    <FileText className="w-4 h-4 text-[#20231F]" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-xs font-bold text-[#20231F] flex items-center gap-2">
                      {doc.title}
                      {doc.required && (
                        <span className="text-[10px] text-[#C84842] font-extrabold">*обязательно</span>
                      )}
                    </div>
                    <div className="text-[11px] text-[#687077] mt-0.5">
                      Файл в деле не привязан: хранилища улик у этого экрана нет.
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3 shrink-0">
                  <span className="text-[11px] text-[#687077] hidden md:inline">
                    Источник: <strong className="text-[#20231F]">не указан</strong>
                  </span>

                  {/* Интерактивный бейдж статуса */}
                  <button
                    onClick={() => cycleDocumentStatus(doc.id)}
                    title="Нажмите для смены статуса"
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold cursor-pointer transition-all ${
                      doc.status === 'ready'
                        ? 'bg-[#E6F4EA] text-[#0B8B5E] border border-[#0B8B5E]/30 hover:bg-[#D4EDDA]'
                        : doc.status === 'review'
                        ? 'bg-[#FEF4E6] text-[#D78416] border border-[#D78416]/30 hover:bg-[#FEEAD1]'
                        : doc.status === 'missing'
                        ? 'bg-[#FCE8E6] text-[#C84842] border border-[#C84842]/30 hover:bg-[#FAD2CF]'
                        : 'bg-[#F8F7F2] text-[#687077] border border-[#E5E1D6] hover:bg-[#F1EEE4]'
                    }`}
                  >
                    {doc.status === 'ready' && '✓ Готов'}
                    {doc.status === 'review' && '⚡ На проверке'}
                    {doc.status === 'missing' && '✕ Отсутствует'}
                    {doc.status === 'unknown' && '— Не отмечен'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ВКЛАДКА 3: ПЛАН ДЕЛА И МАЙЛСТОУНЫ */}
      {activeTab === 'plan' && (
        <div className="bg-white border border-[#E5E1D6] rounded-2xl p-6 shadow-xs space-y-6">
          <div>
            <h2 className="text-lg font-bold text-[#20231F]">
              План дела и процессуальные майлстоуны
            </h2>
            <p className="text-xs text-[#687077] mt-0.5 leading-relaxed">
              Пошаговая дорожная карта подготовки и подачи пакета в консульство/USCIS.
            </p>
          </div>

          {/* 🔴 УБРАНЫ ЧЕТЫРЕ МАЙЛСТОУНА С ДАТАМИ. «Завершено 1 сентября»,
              «В работе • Срок 20 сентября», «Запланировано на 5 октября» стояли
              под ЛЮБЫМ делом и читались как ход ЭТОГО дела. Ни одна из этих
              дат ниоткуда не бралась: план дела на бэкенде не хранится. */}
          <div className="p-6 rounded-xl border border-dashed border-[#E5E1D6] bg-[#F8F7F2] text-xs text-[#687077] leading-relaxed">
            {ISTOCHNIKA_NET} Пока плана не завели, здесь показывается его
            отсутствие, а не образец чужого плана.
          </div>
        </div>
      )}
    </div>
  );
};

export default CaseWorkstation;
